import React, { useEffect, useMemo, useState } from 'react';
import { Empty, Spin } from 'antd';
import { Eye } from 'lucide-react';
import dayjs from 'dayjs';
import LmsModal from '@components/shares/Modal/LmsModal';
import { resourceService } from '@libs/axios';
import { getH5PUrl } from '@utils/H5PUtil';

export interface PracticeAttemptQuestion {
  smq_id: number | null;
  sms_id: number | null;
  smqs_id: number | null;
  sub_modules_session_id?: number | null;
  block_id: string;
  block_id_question: string;
  name_question: string;
  type_question: string;
  answer_status: number;
  answer_result: number;
}

export interface PracticeAttemptDetailData {
  id: number;
  student_name: string;
  submodule_name?: string | null;
  pass_questions: number;
  total_questions: number;
  created_date: string;
  updated_date: string;
  questions?: PracticeAttemptQuestion[];
}

type Outcome = 'correct' | 'wrong' | 'unanswered' | 'all';

const OUTCOME_STYLE: Record<Exclude<Outcome, 'all'>, string> = {
  correct: 'bg-[#D1FAE5] text-[#166534]',
  wrong: 'bg-[#FEE2E2] text-[#B91C1C]',
  unanswered: 'bg-[#E4E7EC] text-[#344054]',
};

const FILTER_STYLE: Record<Outcome, { active: string; idle: string }> = {
  all: {
    active: 'border-[#175CD3] bg-[#DBEAFE] text-[#1D4ED8]',
    idle: 'border-[#BFDBFE] bg-white text-[#1D4ED8]',
  },
  correct: {
    active: 'border-[#16A34A] bg-[#D1FAE5] text-[#166534]',
    idle: 'border-[#BBF7D0] bg-white text-[#166534]',
  },
  wrong: {
    active: 'border-[#DC2626] bg-[#FEE2E2] text-[#B91C1C]',
    idle: 'border-[#FECACA] bg-white text-[#B91C1C]',
  },
  unanswered: {
    active: 'border-[#667085] bg-[#E4E7EC] text-[#344054]',
    idle: 'border-[#D0D5DD] bg-white text-[#344054]',
  },
};

const OUTCOME_LABEL: Record<Exclude<Outcome, 'all'>, string> = {
  correct: 'Đúng',
  wrong: 'Sai',
  unanswered: 'Chưa làm',
};

function outcomeOf(question: PracticeAttemptQuestion): Exclude<Outcome, 'all'> {
  if (question.answer_result === 1) return 'correct';
  if (!question.smqs_id || question.answer_status === 0) return 'unanswered';
  return 'wrong';
}

function formatDuration(start?: string, end?: string) {
  if (!start || !end) return '-';
  const totalSeconds = dayjs(end).diff(dayjs(start), 'second');
  if (totalSeconds < 0) return '-';
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}p`);
  if (seconds > 0) parts.push(`${seconds}s`);
  return parts.join(' ') || '0s';
}

function questionViewUrl(question: PracticeAttemptQuestion) {
  if (!question.block_id_question || question.type_question === 'icdl_scorm') return '';
  const sessionId = question.sub_modules_session_id ?? question.sms_id;
  return getH5PUrl(
    question.block_id_question,
    question.type_question,
    process.env.FE_URL,
    sessionId != null ? String(sessionId) : undefined,
    'practice',
    true,
  );
}

type PracticeAttemptDetailProps = {
  open: boolean;
  activityId?: number | null;
  attemptNumber?: number;
  onClose: () => void;
};

const PracticeAttemptDetail: React.FC<PracticeAttemptDetailProps> = ({
  open,
  activityId,
  attemptNumber,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState<PracticeAttemptDetailData | null>(null);
  const [filter, setFilter] = useState<Outcome>('all');
  const [preview, setPreview] = useState<PracticeAttemptQuestion | null>(null);

  useEffect(() => {
    if (!open || !activityId) {
      setAttempt(null);
      setError('');
      setFilter('all');
      setPreview(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError('');
    setAttempt(null);
    setFilter('all');
    setPreview(null);

    resourceService
      .get('/api/report_practice_attempt_detail/', { params: { activity_id: activityId } })
      .then((response) => {
        if (cancelled) return;
        setAttempt(response.data);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Không tải được chi tiết lần làm bài.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, activityId]);

  const questions = attempt?.questions ?? [];
  const counts = useMemo(() => {
    return questions.reduce(
      (acc, question) => {
        acc[outcomeOf(question)] += 1;
        return acc;
      },
      { correct: 0, wrong: 0, unanswered: 0 },
    );
  }, [questions]);

  const visibleQuestions = questions.filter(
    (question) => filter === 'all' || outcomeOf(question) === filter,
  );
  const scorePercent =
    attempt && attempt.total_questions > 0
      ? Math.round((attempt.pass_questions / attempt.total_questions) * 100)
      : 0;
  const previewUrl = preview ? questionViewUrl(preview) : '';
  const subtitle = attempt ? (
    <>
      <span className="font-medium text-gray-800">{attempt.student_name}</span>
      {attempt.submodule_name ? ` · ${attempt.submodule_name}` : ''}
    </>
  ) : undefined;

  return (
    <>
      <LmsModal
        open={open}
        title={`Chi tiết lần làm bài${attemptNumber ? ` ${attemptNumber}` : ''}`}
        subtitle={subtitle}
        onCancel={onClose}
        destroyOnClose
        className="top-[4px]"
        centered
        modalProps={{
          width: 'min(960px, 94vw)',
          zIndex: 1200,
        }}
        contentClassName="!p-0 !border-0 bg-[#F9FAFB]"
        footer={<div />}
      >
        <div>
          {loading ? (
            <div className="flex justify-center py-16">
              <Spin size="large" />
            </div>
          ) : error ? (
            <div className="px-5 py-16 text-center text-red-500">{error}</div>
          ) : !attempt ? (
            <div className="px-5 py-16">
              <Empty description="Không có dữ liệu lần làm bài" />
            </div>
          ) : (
            <>
              <div className="px-5 py-4">
                <div className="grid grid-cols-2 gap-3 border-b bg-white px-5 py-4 sm:grid-cols-4">
                  <div>
                    <div className="text-xs text-gray-500">Điểm</div>
                    <div className="text-lg font-bold text-[#101828]">
                      {attempt.pass_questions}/{attempt.total_questions}
                      <span className="ml-1 text-sm font-medium text-gray-500">({scorePercent}%)</span>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Đúng / Sai / Chưa làm</div>
                    <div className="text-lg font-bold">
                      <span className="text-[#15803D]">{counts.correct}</span>
                      <span className="mx-1 text-gray-400">/</span>
                      <span className="text-[#DC2626]">{counts.wrong}</span>
                      <span className="mx-1 text-gray-400">/</span>
                      <span className="text-[#344054]">{counts.unanswered}</span>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Bắt đầu</div>
                    <div className="text-sm font-medium">
                      {dayjs(attempt.created_date).format('DD/MM/YYYY HH:mm:ss')}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Thời gian làm</div>
                    <div className="text-sm font-medium">
                      {formatDuration(attempt.created_date, attempt.updated_date)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 px-5">
                {(
                  [
                    ['all', `Tất cả (${questions.length})`],
                    ['correct', `Đúng (${counts.correct})`],
                    ['wrong', `Sai (${counts.wrong})`],
                    ['unanswered', `Chưa làm (${counts.unanswered})`],
                  ] as [Outcome, string][]
                ).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFilter(key)}
                    className={`rounded-full border px-3 py-1 text-sm font-medium ${
                      filter === key ? FILTER_STYLE[key].active : FILTER_STYLE[key].idle
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="space-y-2 px-5 py-4">
                {visibleQuestions.length === 0 ? (
                  <Empty description="Không có câu hỏi trong nhóm này" />
                ) : (
                  visibleQuestions.map((question, index) => {
                    const outcome = outcomeOf(question);
                    const order = questions.indexOf(question) + 1;
                    const canPreview = Boolean(questionViewUrl(question));
                    return (
                      <div
                        key={`${question.smq_id}-${question.sms_id}-${index}`}
                        className="flex items-center gap-3 rounded-lg border border-[#E4E7EC] bg-white px-4 py-3"
                      >
                        <span className="shrink-0 rounded bg-[#DBEAFE] px-3 py-1 text-sm font-semibold text-[#1D4ED8]">
                          Câu {order}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-[#101828]">
                          {question.name_question || question.type_question || 'Câu hỏi'}
                        </span>
                        <span
                          className={`shrink-0 rounded-full px-3 py-1 text-sm font-medium ${OUTCOME_STYLE[outcome]}`}
                        >
                          {OUTCOME_LABEL[outcome]}
                        </span>
                        {canPreview && (
                          <button
                            type="button"
                            className="shrink-0 rounded-md p-1.5 text-[#175CD3] hover:bg-blue-50"
                            aria-label={`Xem câu ${order}`}
                            onClick={() => setPreview(question)}
                          >
                            <Eye size={16} />
                          </button>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>
      </LmsModal>

      <LmsModal
        open={Boolean(preview && previewUrl)}
        title={`Câu ${preview ? questions.indexOf(preview) + 1 : ''}`}
        onCancel={() => setPreview(null)}
        destroyOnClose
        className="top-[4px]"
        centered
        modalProps={{
          width: 'min(1100px, 96vw)',
          zIndex: 1300,
        }}
        contentClassName="!border-0"
      >
        {previewUrl && (
          <iframe
            src={previewUrl}
            title={preview?.name_question || 'Chi tiết câu hỏi'}
            className="w-full rounded border border-gray-200"
            style={{ minHeight: '70vh' }}
          />
        )}
      </LmsModal>
    </>
  );
};

export default PracticeAttemptDetail;
